import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MainHeading from '@/components/common/MainHeading';
import Layout from '@/components/Layout/Layout';
import { IFormData, IFormSection } from '@/interfaces/form.model';
import { FormService } from '@/services/FormService';
import { showToastNotification } from '@/utility/helper';
import Button from '@/components/ui/Button';
import FormMetadataConfig from './FormMetadataConfig';
import SectionManager from './SectionManager';
import FormPreview from './FormPreview';
import { RoutesLink } from '@/utility/RouteLinks';

const initialForm: IFormData = {
  id: '',
  name: '',
  description: '',
  status: 'draft',
  sections: [],
};

const FormBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isViewMode = queryParams.get('mode') === 'view';

  const [formData, setFormData] = useState<IFormData>(initialForm);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(1);

  const fetchFormDetails = async () => {
    setIsLoading(true);
    try {
      // In a real scenario, FormService.getForms would accept ID or there would be getFormById
      // Mocking fetch process assuming ID fetch
      const response = await FormService.getForms({});
      if (response?.status) {
        const forms = response.data?.officeForm || [];
        const found = forms.find(f => f.id === id);
        if (found) {
          setFormData(found);
        } else {
          setFormData({
            id: id || '',
            name: `Form ${id}`,
            description: 'Loaded for edit',
            status: 'draft',
            sections: []
          });
        }
      }
    } catch (error: unknown) {
      showToastNotification('danger',(error as Error)?.message || 'Error fetching form detail', );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchFormDetails();
    } else {
      setFormData({ ...initialForm, id: Math.random().toString(), createdAt: new Date().toISOString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleMetadataChange = (field: keyof IFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdateSections = (sections: IFormSection[]) => {
    // Also re-align sequence numbers
    const sequenced = sections.map((s, i) => ({ ...s, sequence: i + 1 }));
    setFormData(prev => ({ ...prev, sections: sequenced }));
  };

  const handleSave = async () => {
    if (!formData.name) {
      // showToastNotification('Form Name is required', 'danger');
      return;
    }
    setIsSaving(true);
    try {
      let response;
      if (id) {
        // response = await FormService.updateForm(id, formData);
      } else {
        response = await FormService.createForm(formData);
      }

      if (response?.status) {
        showToastNotification('success', response.message);
        navigate(RoutesLink.forms);
      }
    } catch (error: any) {
      console.log(error);
      showToastNotification('danger', error?.message || 'Error saving form');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <MainHeading
        title={isViewMode ? 'View Form' : id ? 'Edit Form' : 'Create Form'}
        showbBackButton
      />

      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="row g-4 h-100 pb-4">
          {/* Builder Panel (60%) */}
          <div className="col-lg-7">
            {/* Steps Navigator */}
            <div className="d-flex gap-3 mb-4">
              <button
                className={`btn flex-grow-1 ${activeStep === 1 ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveStep(1)}
              >
                1. Basic Info
              </button>
              <button
                className={`btn flex-grow-1 ${activeStep === 2 ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveStep(2)}
                disabled={!formData.name}
              >
                2. Form Structure
              </button>
            </div>

            {activeStep === 1 ? (
              <FormMetadataConfig
                formData={formData}
                onChange={handleMetadataChange}
                onNext={() => setActiveStep(2)}
                isViewMode={isViewMode}
              />
            ) : (
              <SectionManager
                sections={formData.sections || []}
                onUpdateSections={handleUpdateSections}
                isViewMode={isViewMode}
              />
            )}

            {!isViewMode && (
              <div className="card shadow-sm border-0 mt-4 bg-light">
                <div className="card-body p-4 d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">Ready to deploy?</h6>
                    <small className="text-muted">Review the preview before saving.</small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      className="btn btn-secondary px-4"
                      onClick={() => navigate(RoutesLink.forms)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="btn btn-success px-4"
                      onClick={handleSave}
                      isLoading={isSaving}
                      disabled={!formData.name || !formData.sections?.length}
                    >
                      Save Form
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Live Preview Panel (40%) */}
          <div className="col-lg-5">
            <div className="position-sticky top-0" style={{ paddingTop: '10px' }}>
              <FormPreview formData={formData} />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FormBuilder;
